import { type PackageDetail } from '@utazzo/shared';
import { UzAlert, UzButton, UzCombobox } from '@utazzo/ui';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { useSetPackageExclusions } from '../use-packages';

import {
  useCreateInclusionExclusion,
  useInclusionsExclusionsList,
} from '@/features/inclusions-exclusions/use-inclusions-exclusions';

interface Selected {
  inclusionExclusionId: string;
  title: string;
}

const seed = (items: PackageDetail['exclusions']): Selected[] =>
  items
    .filter((e) => e.inclusionExclusionId !== null)
    .map((e) => ({ inclusionExclusionId: e.inclusionExclusionId as string, title: e.label }));

interface ExclusionsEditorProps {
  pkg?: PackageDetail | null;
  draftValue?: string[];
  onDraftChange?: (ids: string[]) => void;
}

export const ExclusionsEditor = ({
  pkg,
  draftValue,
  onDraftChange,
}: ExclusionsEditorProps): React.JSX.Element => {
  const isControlled = onDraftChange !== undefined;

  const masterQuery = useInclusionsExclusionsList({
    page: 1,
    pageSize: 200,
    sortBy: 'title',
    sortDir: 'asc',
    type: 'exclusion',
    isActive: true,
  });
  // Always call unconditionally; pkg?.id falls back to '' in controlled mode.
  const set = useSetPackageExclusions(pkg?.id ?? '');
  const createMaster = useCreateInclusionExclusion();
  const master = useMemo(() => masterQuery.data?.data ?? [], [masterQuery.data]);

  // ── Controlled (create) mode ──────────────────────────────────────────────
  const [draftSelected, setDraftSelected] = useState<Selected[]>(() => {
    if (!isControlled) return [];
    const ids = draftValue ?? [];
    return ids.map((id) => ({ inclusionExclusionId: id, title: id }));
  });

  useEffect(() => {
    if (!isControlled) return;
    const ids = draftValue ?? [];
    setDraftSelected(
      ids.map((id) => {
        const entry = master.find((m) => m.id === id);
        return { inclusionExclusionId: id, title: entry?.title ?? id };
      }),
    );
  }, [isControlled, draftValue, master]);

  // ── Live (edit) mode ──────────────────────────────────────────────────────
  const [liveSelected, setLiveSelected] = useState<Selected[]>(() =>
    pkg ? seed(pkg.exclusions) : [],
  );
  useEffect(() => {
    if (isControlled) return;
    if (pkg) setLiveSelected(seed(pkg.exclusions));
  }, [isControlled, pkg]);

  const selected = isControlled ? draftSelected : liveSelected;
  const setSelected = isControlled ? setDraftSelected : setLiveSelected;

  const current = useMemo(() => (pkg ? seed(pkg.exclusions) : []), [pkg]);
  const dirty =
    !isControlled &&
    (selected.length !== current.length ||
      selected.some((s, i) => s.inclusionExclusionId !== current[i]?.inclusionExclusionId));

  const options = useMemo(
    () =>
      master
        .filter((m) => !selected.some((s) => s.inclusionExclusionId === m.id))
        .map((m) => ({ value: m.id, label: m.title })),
    [master, selected],
  );

  const add = (id: string): void => {
    const entry = master.find((m) => m.id === id);
    if (!entry) return;
    setSelected((cur) => {
      if (cur.some((s) => s.inclusionExclusionId === id)) return cur;
      const next = [...cur, { inclusionExclusionId: entry.id, title: entry.title }];
      if (isControlled) onDraftChange(next.map((s) => s.inclusionExclusionId));
      return next;
    });
  };
  const remove = (id: string): void =>
    setSelected((cur) => {
      const next = cur.filter((s) => s.inclusionExclusionId !== id);
      if (isControlled) onDraftChange(next.map((s) => s.inclusionExclusionId));
      return next;
    });
  const move = (index: number, dir: -1 | 1): void =>
    setSelected((cur) => {
      const next = index + dir;
      if (next < 0 || next >= cur.length) return cur;
      const copy = [...cur];
      const a = copy[index];
      const b = copy[next];
      if (!a || !b) return cur;
      copy[index] = b;
      copy[next] = a;
      if (isControlled) onDraftChange(copy.map((s) => s.inclusionExclusionId));
      return copy;
    });

  // Create a brand-new exclusion master entry inline, then select it.
  const createExclusion = async (title: string): Promise<void> => {
    try {
      const m = await createMaster.mutateAsync({ type: 'exclusion', title });
      setSelected((cur) => {
        if (cur.some((s) => s.inclusionExclusionId === m.id)) return cur;
        const next = [...cur, { inclusionExclusionId: m.id, title: m.title }];
        if (isControlled) onDraftChange(next.map((s) => s.inclusionExclusionId));
        return next;
      });
    } catch {
      /* error surfaced by the create hook's toast */
    }
  };

  const save = (): void => {
    void set.mutateAsync({
      items: selected.map((s) => ({ inclusionExclusionId: s.inclusionExclusionId })),
    });
  };

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-zinc-900">Exclusions</h3>
        <p className="text-xs text-zinc-500">
          What is NOT covered. Manage the entries themselves under Masters → Inclusions &
          Exclusions.
        </p>
      </div>

      {masterQuery.isError && <UzAlert tone="destructive">Failed to load exclusions.</UzAlert>}
      {!masterQuery.isLoading && master.length === 0 && !masterQuery.isError && (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          No exclusion entries exist yet. Create them under Masters → Inclusions & Exclusions.
        </p>
      )}

      <UzCombobox
        placeholder="Add an exclusion…"
        searchPlaceholder="Search or type to create…"
        value=""
        onValueChange={add}
        options={options}
        onCreate={(title) => void createExclusion(title)}
        createLabel={(q) => `Create exclusion “${q}”`}
        existingLabels={master.map((m) => m.title)}
        loading={masterQuery.isLoading}
        disabled={
          (isControlled ? false : set.isPending) || masterQuery.isLoading || createMaster.isPending
        }
        emptyText="No matching exclusions"
      />

      {selected.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          None selected yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {selected.map((s, index) => (
            <li
              key={s.inclusionExclusionId}
              className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2 text-sm"
            >
              <span className="truncate">{s.title}</span>
              <div className="ml-auto flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={(isControlled ? false : set.isPending) || index === 0}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-40"
                  aria-label="Move up"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={(isControlled ? false : set.isPending) || index === selected.length - 1}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:opacity-40"
                  aria-label="Move down"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(s.inclusionExclusionId)}
                  disabled={isControlled ? false : set.isPending}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                  aria-label="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!isControlled && (
        <div className="flex items-center justify-end gap-2">
          <span className="mr-auto text-xs text-zinc-400">
            {dirty ? 'Unsaved changes' : 'Saved'}
          </span>
          <UzButton size="sm" onClick={save} loading={set.isPending} disabled={!dirty}>
            Save exclusions
          </UzButton>
        </div>
      )}
    </section>
  );
};
